import { getSafeSupportAICustomerSourceHref, SUPPORT_AI_CUSTOMER_ACCOUNT_PATHS } from './support-customer-ai';

interface ThreadReply {
  reply_type: string;
  customer_ai_draft?: unknown;
}

/** Client-side privacy fence for a ticket payload with unexpected draft fields. */
export function visibleSupportTicketReplies<T extends ThreadReply>(replies: readonly T[], isAgent: boolean): T[] {
  return isAgent
    ? [...replies]
    : replies.filter(reply => reply.reply_type === 'public' && !reply.customer_ai_draft);
}

interface HumanReviewProjection {
  state?: string | null;
  human_intervention_pending?: boolean | null;
}

/** A handoff alone does not mean the customer has already requested a human. */
export function hasPendingCustomerAIHumanReview(
  status: HumanReviewProjection | null,
  ticket: HumanReviewProjection | null
): boolean {
  return status?.human_intervention_pending === true ||
    status?.state === 'human_requested' ||
    ticket?.human_intervention_pending === true ||
    ticket?.state === 'human_requested';
}

/** Refresh before navigation; never claim a status-only reply ID is visible prematurely. */
export async function resolvePublicReplyNavigation<T extends { id: number; reply_type: string }>(
  replyId: number,
  refresh: () => Promise<boolean>,
  visibleReplies: () => readonly T[]
): Promise<'ready' | 'unavailable'> {
  if (!Number.isSafeInteger(replyId) || replyId <= 0) return 'unavailable';
  try {
    const refreshed = await refresh();
    return refreshed && visibleReplies().some(reply => reply.id === replyId && reply.reply_type === 'public')
      ? 'ready'
      : 'unavailable';
  } catch {
    return 'unavailable';
  }
}

interface CitationSource {
  id: string;
  title: string;
  url: string;
}

const DRAFT_GROUNDING_STATES = new Set([
  'grounded', 'partially_grounded', 'not_required', 'failed', 'unverified'
]);

/** Unknown server grounding states stay unlabeled; never print a raw machine code. */
export function getSupportAIDraftGroundingKey(value: unknown): App.I18n.I18nKey | null {
  return typeof value === 'string' && DRAFT_GROUNDING_STATES.has(value)
    ? `support.customerAI.draft.grounding.${value}` as App.I18n.I18nKey
    : null;
}

/** Recognize only the server's canonical rendered source link with a validated URL. */
export function findCanonicalSupportCitation<T extends CitationSource>(
  label: string,
  href: string,
  sources: readonly T[]
): T | null {
  for (const source of sources) {
    if (!/^src_[0-9a-f]{12}$/.test(source.id)) continue;
    const safeHref = getSafeSupportAICustomerSourceHref(source.url);
    if (!safeHref || safeHref !== href) continue;
    if (label === `Source: ${source.title}` || label === `منبع: ${source.title}`) return source;
  }
  return null;
}

export type SupportDraftCitationPart<T extends CitationSource> =
  | { kind: 'text'; text: string }
  | { kind: 'source'; source: T; href: string | null };

/** Keep draft citation markers as inert text unless they resolve to a safe stored source. */
export function splitSupportDraftCitations<T extends CitationSource>(
  content: string,
  sources: readonly T[]
): SupportDraftCitationPart<T>[] {
  const parts: SupportDraftCitationPart<T>[] = [];
  const marker = /\[\[source:(src_[0-9a-f]{12})\]\]/g;
  let text = '';
  let cursor = 0;
  for (const match of content.matchAll(marker)) {
    text += content.slice(cursor, match.index);
    const source = sources.find(item => item.id === match[1]);
    const href = source && getSafeSupportAICustomerSourceHref(source.url);
    if (source && href) {
      if (text) parts.push({ kind: 'text', text });
      // Drafts are staff-only: an account path would open the staff member's account.
      parts.push({ kind: 'source', source, href: SUPPORT_AI_CUSTOMER_ACCOUNT_PATHS.has(href) ? null : href });
      text = '';
    } else {
      text += match[0];
    }
    cursor = match.index + match[0].length;
  }
  text += content.slice(cursor);
  if (text) parts.push({ kind: 'text', text });
  return parts;
}

/** Retained evidence can survive validation without a matching inline marker. */
export function getAdditionalSupportDraftSources<T extends CitationSource>(
  parts: readonly SupportDraftCitationPart<T>[],
  sources: readonly T[]
): Extract<SupportDraftCitationPart<T>, { kind: 'source' }>[] {
  const seen = new Set(parts.flatMap(part => part.kind === 'source' ? [part.source.id] : []));
  const additional: Extract<SupportDraftCitationPart<T>, { kind: 'source' }>[] = [];
  for (const source of sources) {
    if (!/^src_[0-9a-f]{12}$/.test(source.id)) continue;
    if (seen.has(source.id)) continue;
    const href = getSafeSupportAICustomerSourceHref(source.url);
    if (!href) continue;
    seen.add(source.id);
    // As with inline citations, staff must not open their own account for customer evidence.
    additional.push({ kind: 'source', source, href: SUPPORT_AI_CUSTOMER_ACCOUNT_PATHS.has(href) ? null : href });
  }
  return additional;
}
